import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Download, RotateCcw, ChevronsDown, ChevronsUp, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useSession } from "@/App";
import { logger } from "@/utils/logger";

import { ArchetypeCard, ElementDistribution, LeadershipPattern, ScenarioInsights, GrowthPath } from "@/components/ReportSections";
import { generateReportPdf } from "@/utils/reportPdf";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ReportPage() {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const { session: user, clearSession: logout, updateSession: updateUser } = useSession();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState([]);
  const [showRetakeConfirm, setShowRetakeConfirm] = useState(false);
  const [retakeLoading, setRetakeLoading] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);

  // In simplified flow, participants can always start fresh by going home
  const canRetake = true;
  const retakesRemaining = null; // Not tracked in simplified flow

  // Memoize expensive sort computation. Hoisted above early returns so the hook
  // runs on every render (Rules of Hooks). Defensively handles the pre-fetch state.
  const sortedElements = useMemo(
    () => Object.entries(report?.profile?.scores || {}).sort((a, b) => b[1] - a[1]),
    [report]
  );

  const fetchReport = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/report/${assessmentId}`);
      setReport(response.data);
    } catch (error) {
      logger.error("Error fetching report:", error);
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleRetakeClick = () => {
    setShowRetakeConfirm(true);
  };

  const handleRetakeConfirm = async () => {
    setRetakeLoading(true);
    try {
      // Simply clear session and redirect to home for a fresh start
      toast.success("Ready to start a new assessment!");
      logout();
      setShowRetakeConfirm(false);
      navigate("/");
    } catch (error) {
      logger.error("Error resetting session:", error);
      toast.error("Failed to reset. Please try again.");
    } finally {
      setRetakeLoading(false);
    }
  };

  const handlePrint = useCallback(async () => {
    if (!report || pdfGenerating) return;
    setPdfGenerating(true);
    toast.info("Generating PDF...");
    try {
      await generateReportPdf(report);
      toast.success("PDF downloaded!");
    } catch (err) {
      logger.error("PDF generation error:", err);
      toast.error("Failed to generate PDF. Try again.");
    } finally {
      setPdfGenerating(false);
    }
  }, [report, pdfGenerating]);

  const handleExpandAll = () => {
    setExpandedSections([
      'leadership-pattern',
      'scenario-0',
      'scenario-1',
      'scenario-2',
      'scenario-3',
      'scenario-4',
      'growth-path'
    ]);
  };

  const handleCollapseAll = () => {
    setExpandedSections([]);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 mx-auto" style={{ borderBottomColor: '#FF751F' }}></div>
          <p className="text-lg" style={{ color: '#231F20' }}>Generating your report...</p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-lg" style={{ color: '#231F20' }}>Report not found</p>
          <p className="text-sm text-gray-500">This report may have been deleted or doesn't exist.</p>
          <Button 
            onClick={() => navigate('/home')} 
            className="mt-4"
            style={{ backgroundColor: '#FF751F' }}
          >
            Go to Home
          </Button>
        </div>
      </div>
    );
  }

  const { profile, archetype, archetype_overview, scenario_insights, growth_path, essence_sentence, external_perception, closing_style } = report;

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 p-4 md:p-8">
      <div className="max-w-5xl mx-auto space-y-8 fade-in">
        <ArchetypeCard
          archetype={archetype} profile={profile} essence_sentence={essence_sentence}
          external_perception={external_perception} closing_style={closing_style}
          expandedSections={expandedSections} setExpandedSections={setExpandedSections}
        />
        <ElementDistribution profile={profile} sortedElements={sortedElements} />
        <LeadershipPattern profile={profile} archetype_overview={archetype_overview} expandedSections={expandedSections} setExpandedSections={setExpandedSections} />
        <ScenarioInsights scenario_insights={scenario_insights} expandedSections={expandedSections} setExpandedSections={setExpandedSections} />
        <GrowthPath growth_path={growth_path} expandedSections={expandedSections} setExpandedSections={setExpandedSections} />

        {/* Actions */}
        <div className="space-y-4 pb-8">
          <div className="flex justify-center gap-2 sm:gap-3 flex-wrap px-4">
            <Button data-testid="expand-all-btn" onClick={handleExpandAll} variant="outline"
              className="h-9 sm:h-10 px-4 sm:px-6 text-xs sm:text-sm border-2 hover:bg-orange-50"
              style={{ borderColor: '#FFBD25', color: '#231F20' }}>
              <ChevronsDown className="mr-1 sm:mr-2 w-3 h-3 sm:w-4 sm:h-4" />
              <span className="whitespace-nowrap">Expand All</span>
            </Button>
            <Button data-testid="collapse-all-btn" onClick={handleCollapseAll} variant="outline"
              className="h-9 sm:h-10 px-4 sm:px-6 text-xs sm:text-sm border-2 hover:bg-orange-50"
              style={{ borderColor: '#FFBD25', color: '#231F20' }}>
              <ChevronsUp className="mr-1 sm:mr-2 w-3 h-3 sm:w-4 sm:h-4" />
              <span className="whitespace-nowrap">Collapse All</span>
            </Button>
          </div>
          <div className="flex justify-center gap-2 sm:gap-4 flex-wrap px-4">
            <Button data-testid="download-report-btn" onClick={handlePrint} disabled={pdfGenerating}
              className="h-10 sm:h-12 px-4 sm:px-8 text-sm sm:text-base bg-gradient-to-r from-[#FF751F] to-[#FFBD25] hover:from-[#FF751F] hover:to-[#FFD04A] text-white shadow-lg font-semibold">
              {pdfGenerating ? <Loader2 className="mr-1 sm:mr-2 w-4 h-4 animate-spin" /> : <Download className="mr-1 sm:mr-2 w-4 h-4" />}
              <span className="hidden sm:inline">{pdfGenerating ? "Generating PDF..." : "Download Report"}</span>
              <span className="sm:hidden">{pdfGenerating ? "Generating..." : "Download"}</span>
            </Button>
            {canRetake && (
              <Button data-testid="retake-btn" onClick={handleRetakeClick} variant="outline"
                className="h-10 sm:h-12 px-4 sm:px-8 text-sm sm:text-base border-2 hover:bg-orange-50 font-semibold"
                style={{ borderColor: '#FF751F', color: '#231F20' }}>
                <RotateCcw className="mr-1 sm:mr-2 w-4 h-4" />
                <span className="hidden sm:inline">Take New Assessment</span>
                <span className="sm:hidden">Retake</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      <AlertDialog open={showRetakeConfirm} onOpenChange={setShowRetakeConfirm}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl">Start New Assessment?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>Are you sure you want to take a new assessment?</p>
              {user?.user_type === "manager" && (
                <p className="text-amber-600 font-medium">You have {retakesRemaining} retake{retakesRemaining !== 1 ? 's' : ''} remaining.</p>
              )}
              {user?.user_type === "tester" && (
                <p className="text-green-600 font-medium">Unlimited retakes available. Each assessment will be stored separately.</p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={retakeLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRetakeConfirm} disabled={retakeLoading}
              className="bg-gradient-to-r from-[#FF751F] to-[#FFBD25] text-white">
              {retakeLoading ? "Processing..." : "Yes, Start New Assessment"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}